/* ======================================================================
   Grade de Imagens — comportamento
   O bin-packing (posição lógica de cada item) já vem calculado do PHP,
   de forma determinística. Este script:
     1) Mede a largura real do container e expõe --gd-unit (px de 1
        coluna/banda), que o CSS usa via calc() para converter a posição
        lógica de cada item em px reais (ver grade-imagens.scss).
     2) Corrige analiticamente (sem medir o DOM/imagens) a posição vertical
        dos itens afetados por itens de 6 colunas (full-width), cuja altura
        segue a proporção real da imagem em vez do formato lógico L — ver
        --gd-shift em computeUnit.
     3) Anima a entrada de cada item (campo ACF "Movimento") via
        ScrollTrigger.batch.
     4) Inicializa o lightbox em galeria (PhotoSwipe v5), sob demanda.
   ====================================================================== */

(function () {
  'use strict';

  const PSWP_CORE = 'https://cdn.jsdelivr.net/npm/photoswipe@5/dist/photoswipe.esm.min.js';
  const PSWP_LB = 'https://cdn.jsdelivr.net/npm/photoswipe@5/dist/photoswipe-lightbox.esm.min.js';

  // px — deve casar com $gd-gap: 1rem em grade-imagens.scss (16px @ 1rem=16px)
  const GAP = 16;
  const COLS = 6;

  const prefersReduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── 1) --gd-unit = px de 1 coluna/banda, a partir da largura real ──────
  // Itens de 6 colunas (full-width) usam aspect-ratio da imagem real (ver
  // grade-imagens.scss), então sua altura renderizada pode diferir da altura
  // "alocada" pelo bin-packing (que assume o formato L declarado). Aqui
  // corrigimos isso analiticamente (sem medir nada no DOM): cada item recebe
  // --gd-shift = soma das diferenças (real - alocada) de todo item 6-col
  // cujo intervalo de linhas termina ANTES da posição lógica deste item —
  // não pela ordem no DOM, pois o bin-packing pode preencher uma lacuna
  // anterior com um item cadastrado depois.
  function computeUnit(grid) {
    const width = grid.clientWidth;
    const unit = (width - GAP * (COLS - 1)) / COLS;
    if (unit <= 0) return;
    grid.style.setProperty('--gd-unit', unit + 'px');

    const items = grid.querySelectorAll('.gd-item');
    const fullWidthItems = [];

    items.forEach((el) => {
      if (el.dataset.col !== '6') return;
      const row = parseFloat(el.dataset.row) || 1;
      const rowStart = parseFloat(el.dataset.rowStart) || 0;
      const ratio = parseFloat(el.dataset.ratio) || 6 / row; // fallback: proporção do formato CxL

      const allocatedHeight = row * unit + (row - 1) * GAP;
      const naturalHeight = width / ratio;

      fullWidthItems.push({
        boundaryRow: rowStart + row,
        shift: naturalHeight - allocatedHeight,
      });
    });

    let totalShift = 0;
    items.forEach((el) => {
      const rowStart = parseFloat(el.dataset.rowStart) || 0;
      let shift = 0;
      fullWidthItems.forEach((fw) => {
        if (fw.boundaryRow <= rowStart) shift += fw.shift;
      });
      el.style.setProperty('--gd-shift', shift + 'px');
    });
    fullWidthItems.forEach((fw) => { totalShift += fw.shift; });
    grid.style.setProperty('--gd-shift-total', totalShift + 'px');
  }

  function refreshTriggers() {
    if (typeof window.ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
  }

  // ── 2) Entrada por item via ScrollTrigger.batch ─────────────────────────
  // Chaves = slugs emitidos em data-movimento (ver gd_movimento_slug no PHP;
  // o campo ACF em si continua com os rótulos por extenso em português).
  const MOVIMENTO_OFFSET = {
    'fade-t2b': { y: -60 }, // topo para baixo
    'fade-b2t': { y: 60 }, // baixo para topo
    'fade-l2r': { x: -60 }, // esquerda para direita
    'fade-r2l': { x: 60 }, // direita para esquerda
  };

  function initMovimento(grid) {
    if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') return;
    if (prefersReduced) return; // acessibilidade: sem animação, itens já visíveis

    gsap.registerPlugin(ScrollTrigger);

    const items = [];
    grid.querySelectorAll('.gd-item[data-movimento]').forEach((el) => {
      const offset = MOVIMENTO_OFFSET[el.dataset.movimento];
      if (!offset) return; // "Sem movimento" ou spacer
      el._gdOx = offset.x || 0;
      el._gdOy = offset.y || 0;
      items.push(el);
    });
    if (!items.length) return;

    // desloca via transform (x/y) — não mexe em left/top (posição estática)
    items.forEach((el) => gsap.set(el, { x: el._gdOx, y: el._gdOy, opacity: 0 }));

    ScrollTrigger.batch(items, {
      start: 'top 85%',
      onEnter: (batch) =>
        gsap.to(batch, {
          x: 0,
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          stagger: 0.08,
          overwrite: true,
        }),
      onLeaveBack: (batch) =>
        batch.forEach((el) =>
          gsap.to(el, {
            x: el._gdOx,
            y: el._gdOy,
            opacity: 0,
            duration: 0.4,
            ease: 'power2.in',
            overwrite: true,
          }),
        ),
    });
  }

  // ── 3) Lightbox PhotoSwipe (galeria por grade), sob demanda ─────────────
  let pswpModulesPromise = null;
  function loadPhotoSwipe() {
    if (!pswpModulesPromise) {
      pswpModulesPromise = import(PSWP_LB).then((m) => m.default || m);
    }
    return pswpModulesPromise;
  }

  function initLightbox(grid) {
    if (!grid.querySelector('a.gd-link[data-pswp-width]')) return;

    loadPhotoSwipe()
      .then((PhotoSwipeLightbox) => {
        const lightbox = new PhotoSwipeLightbox({
          gallery: grid,
          children: 'a.gd-link',
          pswpModule: () => import(PSWP_CORE),
          bgOpacity: 0.92,
        });

        lightbox.on('uiRegister', function () {
          lightbox.pswp.ui.registerElement({
            name: 'gd-caption',
            order: 9,
            isButton: false,
            appendTo: 'root',
            html: '',
            onInit: (el, pswp) => {
              el.className = 'pswp__gd-caption';
              const update = () => {
                const slideEl = pswp.currSlide && pswp.currSlide.data.element;
                const cap = slideEl ? slideEl.getAttribute('data-pswp-caption') : '';
                el.innerHTML = cap ? cap : '';
                el.style.display = cap ? 'block' : 'none';
              };
              pswp.on('change', update);
              update();
            },
          });
        });

        lightbox.init();
      })
      .catch(() => {
        /* falha de rede: os <a> continuam abrindo a imagem cheia (fallback nativo) */
      });
  }

  function scheduleLightbox(grid) {
    const start = () => initLightbox(grid);
    if ('requestIdleCallback' in window) {
      requestIdleCallback(start, { timeout: 2500 });
    } else {
      setTimeout(start, 1500);
    }
  }

  // ── Boot ────────────────────────────────────────────────────────────────
  function init() {
    const grids = document.querySelectorAll('[data-gd-masonry]');
    if (!grids.length) return;

    grids.forEach((grid) => {
      computeUnit(grid); // geometria primeiro (estável p/ os triggers do GSAP)
      initMovimento(grid);
      scheduleLightbox(grid);

      // recalcula em mudança de largura (breakpoint, scrollbar, etc.)
      let raf = 0;
      const relayout = () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          computeUnit(grid);
          refreshTriggers();
        });
      };
      window.addEventListener('resize', relayout, { passive: true });
      if ('ResizeObserver' in window) {
        new ResizeObserver(relayout).observe(grid);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
